import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../../app/store'
import { getAuthToken } from '../../utils/secureStorage'

// Types for discount operations
export interface DiscountCodeRequest {
  code: string
  userId?: number
  customerId?: number
}

export interface DiscountCodeResponse {
  success: boolean
  data: {
    code: string
    discount_amount: number
    discount_percentage?: number
    valid: boolean
    message?: string
  }
  message?: string
}

export interface RemoveDiscountRequest {
  userId?: number
  customerId?: number
}

export interface RemoveDiscountResponse {
  success: boolean
  data: {
    removed: boolean
    message?: string
  }
  message?: string
}

// Discount API
export const discountApi = createApi({
  reducerPath: 'discountApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/discount',
    prepareHeaders: (headers, { getState }) => {
      // Try to get token from Redux state first
      let token = (getState() as RootState).auth.token
      
      // If not in Redux state (e.g., after page refresh before rehydration), get from secure storage
      if (!token) {
        token = getAuthToken()
      }
      
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Discount'],
  endpoints: (builder) => ({
    // Validate discount code (equivalent to Flutter's validateDiscountCode)
    validateDiscountCode: builder.mutation<DiscountCodeResponse, DiscountCodeRequest>({
      query: ({ code, userId, customerId }) => ({
        url: '/validate',
        method: 'POST',
        body: { 
          code,
          user_id: userId,
          customer_id: customerId
        },
      }),
      invalidatesTags: ['Discount'],
    }),

    // Remove discount code (equivalent to Flutter's removeDiscount)
    removeDiscount: builder.mutation<RemoveDiscountResponse, RemoveDiscountRequest>({
      query: ({ userId, customerId }) => ({
        url: '/remove',
        method: 'POST',
        body: { 
          user_id: userId,
          customer_id: customerId
        },
      }),
      invalidatesTags: ['Discount'],
    }),
  }),
})

// Export hooks for usage in functional components
export const {
  useValidateDiscountCodeMutation,
  useRemoveDiscountMutation,
} = discountApi
